import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useDocumentMeta } from '../hooks/useDocumentMeta';
import { MapPinIcon, HomeIcon, StarIcon, Heart, HandHeart, Users, Activity, AlertTriangle, HeartHandshake, HeartPulseIcon, ShoppingBasket, Stethoscope, Building2, BatteryLow, Radio, Pill, HandCoins, Share2, HeartCrack, Globe2 } from 'lucide-react';
const LOGO = 'https://cdn.magicpatterns.com/uploads/owciZka94T3FY4LQCQ9qcS/pancita_feliz.png';
const HERO_BG = 'https://cdn.magicpatterns.com/uploads/tJ9Qyo76BSSU8qBwrWJ4Ft/ChatGPT_Image_Jun_29,_2026,_01_10_38_PM.png';
const stats = [{
  number: '+12,000',
  label: 'Desplazados',
  color: '#7DC920',
  border: true,
  icon: Users,
  iconColor: '#7DC920'
}, {
  number: '+1,500',
  label: 'Fallecidos',
  color: '#ff6b6b',
  border: true,
  icon: HeartCrack,
  iconColor: '#ff6b6b'
}, {
  number: '24+',
  label: 'Países',
  color: '#a8e063',
  border: true,
  icon: Globe2,
  iconColor: '#a8e063'
}, {
  number: '7.5',
  label: 'Magnitud',
  color: '#7DC920',
  border: false,
  icon: Activity,
  iconColor: '#7DC920'
}];
const timeline = [{
  dot: '#2d5a1b',
  time: '24 Jun · Hora 0',
  title: 'Primer Terremoto 7.2',
  text: 'Epicentro al oeste de Caracas, entre Carabobo y Yaracuy'
}, {
  dot: '#2d5a1b',
  time: '24 Jun · +39 segundos',
  title: 'Segundo Terremoto 7.5',
  text: 'El más fuerte, causó el colapso masivo de edificios residenciales'
}, {
  dot: '#a8e063',
  time: '24 Jun · Horas después',
  title: 'Alerta de Tsunami',
  text: 'Emitida para el Caribe, cancelada horas después sin daños por tsunami'
}, {
  dot: '#7DC920',
  time: '29 Jun',
  title: 'Réplica 4.6',
  text: 'Al norte de Caracas, sin daños adicionales importantes'
}, {
  dot: '#7DC920',
  time: '29 Jun · 106 horas después',
  title: 'Rescate Milagroso',
  text: 'Joven de 21 años encontrado con vida tras 106 horas bajo los escombros'
}];
const zones = [{
  title: 'Caracas',
  text: 'Capital. Colapso de edificios y daños en infraestructura',
  badge: 'CRÍTICO',
  badgeBg: '#e8f5e0',
  badgeColor: '#2d5a1b'
}, {
  title: 'La Guaira',
  text: 'Zona costera con daños severos en puerto e infraestructura',
  badge: 'CRÍTICO',
  badgeBg: '#e8f5e0',
  badgeColor: '#2d5a1b'
}, {
  title: 'Carabobo',
  text: 'Daños en hospitales y carreteras principales',
  badge: 'GRAVE',
  badgeBg: '#f0f9e0',
  badgeColor: '#5a8a1b'
}, {
  title: 'Yaracuy',
  text: 'Derrumbes y cortes de electricidad y comunicaciones',
  badge: 'GRAVE',
  badgeBg: '#f0f9e0',
  badgeColor: '#5a8a1b'
}];
const needs = [{
  icon: ShoppingBasket,
  title: 'Alimentación',
  text: 'Familias sin acceso a comida ni agua potable en zonas devastadas'
}, {
  icon: Stethoscope,
  title: 'Atención Médica',
  text: 'Hospitales dañados y más de 3,000 heridos necesitan atención urgente'
}, {
  icon: Building2,
  title: 'Refugio',
  text: 'Más de 12,000 desplazados sin hogar necesitan albergue temporal seguro'
}, {
  icon: BatteryLow,
  title: 'Electricidad',
  text: 'Cortes masivos de luz afectan hospitales y comunicaciones de emergencia'
}, {
  icon: Radio,
  title: 'Comunicaciones',
  text: 'Zonas incomunicadas dificultan las labores de rescate y localización de desaparecidos'
}, {
  icon: Pill,
  title: 'Medicamentos',
  text: 'Escasez crítica de medicinas e insumos médicos en toda la zona afectada'
}];
const sectionLabel: React.CSSProperties = {
  fontSize: '12px',
  color: '#7DC920',
  letterSpacing: '3px',
  fontWeight: 700,
  textAlign: 'center',
  marginBottom: '12px',
  textTransform: 'uppercase'
};
const fieldStyle: React.CSSProperties = {
  width: '100%',
  padding: '14px 16px',
  border: '1px solid #e0e0e0',
  borderRadius: '10px',
  fontSize: '15px',
  marginBottom: '16px',
  outline: 'none'
};
export function VenezuelaEmergencia() {
  const [formSubmitted, setFormSubmitted] = useState(false);
  useDocumentMeta({
    title: 'Emergencia Venezuela | Pancita Feliz Foundation',
    description: 'Venezuela necesita tu ayuda. Dos terremotos devastaron el país dejando más de 12,000 familias sin hogar. Dona ahora.',
    path: '/venezuela-emergencia'
  });
  const handleVolunteerSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    try {
      await fetch(form.action, {
        method: 'POST',
        body: new FormData(form),
        headers: {
          Accept: 'application/json'
        }
      });
      setFormSubmitted(true);
      form.reset();
    } catch {
      setFormSubmitted(true);
    }
  };
  return <main className="w-full" data-id="element-431">
      {/* SECTION 1: HERO */}
      <section className="mp-emg-hero-section relative overflow-hidden flex items-center justify-center" style={{
      minHeight: '100vh',
      backgroundColor: '#111111'
    }} data-id="element-432">
        {/* Background image layer */}
        <div className="absolute inset-0" style={{
        backgroundImage: `url('${HERO_BG}')`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        zIndex: 0
      }} data-id="element-433" />
        {/* Neutral cinematic overlay with vignette */}
        <div className="absolute inset-0" style={{
        background: 'radial-gradient(circle at center, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.85) 100%)',
        zIndex: 1
      }} data-id="element-434" />

        {/* Content */}
        <div className="mp-emg-hero-content relative flex flex-col items-center justify-center text-center w-full px-4 animate-fade-in-down" style={{
        zIndex: 2,
        maxWidth: '760px',
        paddingTop: '100px',
        paddingBottom: '60px'
      }} data-id="element-435">
          <img src={LOGO} alt="Pancita Feliz Foundation" className="mp-emg-hero-logo" style={{
          width: '200px',
          maxWidth: '200px',
          height: 'auto',
          filter: 'brightness(0) invert(1)',
          display: 'block',
          margin: '0 auto 28px auto'
        }} data-id="element-436" />

          <span className="mp-emg-pulse flex items-center gap-2" style={{
          background: '#C94A44',
          color: 'white',
          fontSize: '13px',
          fontWeight: 600,
          letterSpacing: '1px',
          padding: '8px 20px',
          borderRadius: '9999px',
          display: 'inline-flex',
          marginBottom: '24px'
        }} data-id="element-437">
            <span data-id="element-438">⚠️</span> EMERGENCIA ACTIVA
          </span>

          <div className="relative mb-6" data-id="element-439">
            <h1 className="mp-emg-hero-title" style={{
            fontSize: 'clamp(40px, 8vw, 72px)',
            fontWeight: 800,
            color: '#FFFFFF',
            lineHeight: 1.1,
            letterSpacing: '-0.02em',
            margin: 0
          }} data-id="element-440">
              Venezuela Nos Necesita
            </h1>
            <div className="mp-emg-hero-tagline" style={{
            fontFamily: "'Caveat', cursive",
            color: '#7AC943',
            fontSize: 'clamp(24px, 4vw, 36px)',
            position: 'absolute',
            right: '-20px',
            bottom: '-20px',
            transform: 'rotate(-5deg)',
            whiteSpace: 'nowrap'
          }} data-id="element-441">
              Hoy más que nunca{' '}
              <HandHeart size={28} color="#7DC920" style={{
              display: 'inline-block',
              verticalAlign: 'middle'
            }} data-id="element-442" />
            </div>
          </div>

          <p className="mp-emg-hero-subtitle" style={{
          fontSize: '18px',
          color: 'rgba(255,255,255,0.9)',
          maxWidth: '620px',
          margin: '0 auto 40px',
          lineHeight: 1.8,
          fontWeight: 400
        }} data-id="element-443">
            Dos terremotos de magnitud 7.2 y 7.5 han dejado miles de familias
            sin hogar y con necesidades urgentes.
            <br data-id="element-444" />
            <br data-id="element-445" />
            Tu ayuda puede llevar esperanza, alimento, refugio y apoyo a cada
            familia afectada.
          </p>

          {/* Impact Icons */}
          <div className="mp-emg-hero-pills flex flex-wrap justify-center gap-6 md:gap-8 mb-10" data-id="element-446">
            {[{
            icon: Users,
            text: 'Para todas las familias'
          }, {
            icon: HeartPulseIcon,
            text: 'Apoyo integral'
          }, {
            icon: HeartHandshake,
            text: 'Esperanza que une'
          }, {
            icon: MapPinIcon,
            text: 'Donde más se necesita'
          }].map((item, i) => <div key={i} className="flex items-center gap-2" data-id="element-447">
                <item.icon size={20} color="#7AC943" strokeWidth={1.5} data-id="element-448" />
                <span style={{
              color: 'white',
              fontSize: '14px',
              fontWeight: 500
            }} data-id="element-449">
                  {item.text}
                </span>
              </div>)}
          </div>

          {/* CTAs */}
          <div className="mp-emg-hero-btns flex flex-col sm:flex-row justify-center gap-4 w-full mb-16" data-id="element-450">
            <Link to="/donar-venezuela" className="mp-emg-hero-btn group relative overflow-hidden flex flex-col items-center justify-center transition-transform hover:-translate-y-1" style={{
            background: '#7AC943',
            color: 'white',
            borderRadius: '16px',
            height: '72px',
            padding: '0 40px',
            textDecoration: 'none',
            boxShadow: '0 10px 30px rgba(122, 201, 67, 0.3)',
            minWidth: '280px'
          }} data-id="element-451">
              <div className="flex items-center gap-2 font-bold text-lg" data-id="element-452">
                <Heart size={20} fill="currentColor" data-id="element-453" /> Donar Ahora
              </div>
              <div style={{
              fontSize: '12px',
              opacity: 0.9,
              fontWeight: 500,
              marginTop: '2px'
            }} data-id="element-454">
                Cada aporte hace la diferencia
              </div>
            </Link>

            <a href="#voluntario" className="mp-emg-hero-btn group relative overflow-hidden flex flex-col items-center justify-center transition-transform hover:-translate-y-1 hover:bg-white/10" style={{
            background: 'transparent',
            color: 'white',
            borderRadius: '16px',
            height: '72px',
            padding: '0 40px',
            border: '1px solid rgba(255,255,255,0.4)',
            textDecoration: 'none',
            minWidth: '280px'
          }} data-id="element-455">
              <div className="flex items-center gap-2 font-bold text-lg" data-id="element-456">
                Quiero Ser Voluntario
              </div>
              <div style={{
              fontSize: '12px',
              opacity: 0.7,
              fontWeight: 500,
              marginTop: '2px'
            }} data-id="element-457">
                Únete a nuestra red de ayuda
              </div>
            </a>
          </div>

          {/* Stats Card */}
          <div className="w-full grid grid-cols-2 md:grid-cols-4 gap-6 p-6 md:p-8" style={{
          background: 'rgba(20, 20, 20, 0.6)',
          backdropFilter: 'blur(16px)',
          WebkitBackdropFilter: 'blur(16px)',
          border: '1px solid rgba(255,255,255,0.1)',
          borderRadius: '24px',
          boxShadow: '0 20px 40px rgba(0,0,0,0.4)',
          marginBottom: '24px'
        }} data-id="element-458">
            {[{
            num: '+1,500',
            label: 'Fallecidos',
            icon: AlertTriangle
          }, {
            num: '+3,000',
            label: 'Heridos',
            icon: Activity
          }, {
            num: '+12,000',
            label: 'Desplazados',
            icon: HomeIcon
          }, {
            num: '7.5',
            label: 'Magnitud',
            icon: Activity
          }].map((stat, i) => <div key={i} className="flex flex-col items-center text-center" data-id="element-459">
                <div className="flex items-center justify-center mb-3" style={{
              width: '40px',
              height: '40px',
              borderRadius: '50%',
              background: 'rgba(122, 201, 67, 0.15)',
              color: '#7AC943'
            }} data-id="element-460">
                  <stat.icon size={20} strokeWidth={2} data-id="element-461" />
                </div>
                <div style={{
              color: 'white',
              fontSize: '24px',
              fontWeight: 700,
              marginBottom: '4px'
            }} data-id="element-462">
                  {stat.num}
                </div>
                <div style={{
              color: 'rgba(255,255,255,0.6)',
              fontSize: '13px',
              fontWeight: 500,
              textTransform: 'uppercase',
              letterSpacing: '1px'
            }} data-id="element-463">
                  {stat.label}
                </div>
              </div>)}
          </div>

          {/* Bottom Statement */}
          <div className="flex items-center justify-center gap-2" style={{
          color: 'white',
          fontSize: '14px',
          opacity: 0.8
        }} data-id="element-464">
            <Heart size={14} color="#7AC943" fill="#7AC943" data-id="element-465" />
            <span data-id="element-466">
              Tu solidaridad se transforma en vida, esperanza y reconstrucción.
            </span>
          </div>
        </div>
      </section>

      {/* SECTION 2: STATS BAR */}
      <section style={{
      background: '#1a1a1a',
      borderBottom: '1px solid #333'
    }} data-id="element-467">
        <div className="mp-emg-stats" style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(4, 1fr)',
        maxWidth: '900px',
        margin: '0 auto',
        padding: '32px 20px'
      }} data-id="element-468">
          {stats.map(s => <div key={s.label} className="mp-emg-stat-item" style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          padding: '20px 16px',
          borderRight: s.border ? '1px solid #333' : 'none'
        }} data-id="element-469">
              <s.icon size={24} color={s.iconColor} style={{
            marginBottom: '8px'
          }} data-id="element-470" />
              <div className="mp-emg-stat-num" style={{
            fontSize: '36px',
            fontWeight: 800,
            color: s.color
          }} data-id="element-471">
                {s.number}
              </div>
              <div className="mp-emg-stat-label" style={{
            fontSize: '13px',
            color: '#999'
          }} data-id="element-472">
                {s.label}
              </div>
            </div>)}
        </div>
      </section>

      {/* SECTION 3: WHAT HAPPENED */}
      <section className="mp-emg-what-section" style={{
      background: '#f9f9f9',
      padding: '80px 20px'
    }} data-id="element-473">
        <p style={sectionLabel} data-id="element-474">Lo que ocurrió</p>
        <h2 className="mp-emg-section-title" style={{
        fontSize: '36px',
        fontWeight: 700,
        color: '#1a1a1a',
        textAlign: 'center',
        marginBottom: '48px',
        maxWidth: '600px',
        marginLeft: 'auto',
        marginRight: 'auto'
      }} data-id="element-475">
          El Desastre Más Devastador de Venezuela en Décadas
        </h2>
        <div className="mp-emg-2col" style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '40px',
        maxWidth: '900px',
        margin: '0 auto'
      }} data-id="element-476">
          {/* Left: timeline */}
          <div data-id="element-477">
            <h3 style={{
            fontSize: '18px',
            fontWeight: 700,
            marginBottom: '24px',
            color: '#1a1a1a'
          }} data-id="element-478">
              Cronología
            </h3>
            <div style={{
            borderLeft: '2px dashed #ddd',
            marginLeft: '5px'
          }} data-id="element-479">
              {timeline.map((item, i) => <div key={i} className="flex gap-4" style={{
              marginBottom: '24px',
              marginLeft: '-7px'
            }} data-id="element-480">
                  <div style={{
                width: '12px',
                height: '12px',
                borderRadius: '50%',
                marginTop: '4px',
                flexShrink: 0,
                background: item.dot
              }} data-id="element-481" />
                  <div data-id="element-482">
                    <div style={{
                  fontSize: '11px',
                  color: '#2d5a1b',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  marginBottom: '2px'
                }} data-id="element-483">
                      {item.time}
                    </div>
                    <div style={{
                  fontSize: '15px',
                  fontWeight: 700,
                  color: '#1a1a1a',
                  marginBottom: '4px'
                }} data-id="element-484">
                      {item.title}
                    </div>
                    <div style={{
                  fontSize: '14px',
                  color: '#666',
                  lineHeight: 1.5
                }} data-id="element-485">
                      {item.text}
                    </div>
                  </div>
                </div>)}
            </div>
          </div>

          {/* Right: zones */}
          <div data-id="element-486">
            <h3 style={{
            fontSize: '18px',
            fontWeight: 700,
            marginBottom: '24px',
            color: '#1a1a1a'
          }} data-id="element-487">
              Zonas Afectadas
            </h3>
            {zones.map(z => <div key={z.title} className="flex items-center gap-3" style={{
            background: 'white',
            borderRadius: '12px',
            padding: '16px 20px',
            marginBottom: '12px',
            borderLeft: '4px solid #2d5a1b',
            boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
          }} data-id="element-488">
                <MapPinIcon size={20} color="#2d5a1b" className="shrink-0" data-id="element-489" />
                <div data-id="element-490">
                  <div style={{
                fontSize: '15px',
                fontWeight: 700,
                color: '#1a1a1a'
              }} data-id="element-491">
                    {z.title}
                  </div>
                  <div style={{
                fontSize: '13px',
                color: '#666'
              }} data-id="element-492">
                    {z.text}
                  </div>
                </div>
                <span style={{
              background: z.badgeBg,
              color: z.badgeColor,
              fontSize: '10px',
              fontWeight: 700,
              padding: '2px 8px',
              borderRadius: '10px',
              marginLeft: 'auto',
              flexShrink: 0
            }} data-id="element-493">
                  {z.badge}
                </span>
              </div>)}
          </div>
        </div>
      </section>

      {/* SECTION 4: NEEDS */}
      <section className="mp-emg-needs-section" style={{
      background: 'white',
      padding: '80px 20px'
    }} data-id="element-494">
        <p style={sectionLabel} data-id="element-495">Ayuda urgente necesaria</p>
        <h2 className="mp-emg-section-title" style={{
        fontSize: '36px',
        fontWeight: 700,
        textAlign: 'center',
        marginBottom: '48px',
        color: '#1a1a1a'
      }} data-id="element-496">
          ¿Qué Necesitan las Familias?
        </h2>
        <div className="mp-emg-needs" style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gap: '24px',
        maxWidth: '900px',
        margin: '0 auto'
      }} data-id="element-497">
          {needs.map(({
          icon: Icon,
          title,
          text
        }) => <div key={title} className="mp-emg-need-card" style={{
          background: '#f9f9f9',
          borderRadius: '16px',
          padding: '32px 24px',
          textAlign: 'center',
          borderTop: '4px solid #7DC920'
        }} data-id="element-498">
              <div className="mp-emg-need-icon" style={{
            background: '#e8f5e0',
            borderRadius: '16px',
            width: '64px',
            height: '64px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 16px'
          }} data-id="element-499">
                <Icon size={28} color="#2d5a1b" data-id="element-500" />
              </div>
              <h3 className="mp-emg-need-title" style={{
            fontSize: '18px',
            fontWeight: 700,
            color: '#1a1a1a',
            marginBottom: '8px'
          }} data-id="element-501">
                {title}
              </h3>
              <p className="mp-emg-need-text" style={{
            fontSize: '14px',
            color: '#666',
            lineHeight: 1.5
          }} data-id="element-502">
                {text}
              </p>
            </div>)}
        </div>
      </section>

      {/* SECTION 4b: RESCUE STORY */}
      <section style={{
      background: '#1a1a1a',
      padding: '60px 20px',
      textAlign: 'center'
    }} data-id="element-503">
        <div className="mp-emg-rescue-box" style={{
        background: 'rgba(125,201,32,0.1)',
        border: '1px solid rgba(125,201,32,0.3)',
        borderRadius: '16px',
        padding: '32px',
        maxWidth: '600px',
        margin: '0 auto',
        textAlign: 'center'
      }} data-id="element-504">
          <StarIcon size={32} color="#7DC920" className="mx-auto" style={{
          marginBottom: '16px'
        }} data-id="element-505" />
          <h3 style={{
          fontSize: '20px',
          fontWeight: 700,
          color: 'white',
          marginBottom: '12px'
        }} data-id="element-506">
            Historias de Esperanza
          </h3>
          <p className="mp-emg-rescue-text" style={{
          fontSize: '15px',
          color: 'rgba(255,255,255,0.8)',
          lineHeight: 1.7
        }} data-id="element-507">
            En medio de la tragedia, rescatistas venezolanos e internacionales
            trabajan sin descanso. Cada hora cuenta. Cada vida importa. Tu apoyo
            hace posible que estas historias continúen escribiéndose.
          </p>
        </div>
      </section>

      {/* SECTION 5: FAMILIES THAT NEED HELP */}
      <section className="mp-emg-family-section" style={{
      background: '#1a1a1a',
      padding: '60px 20px'
    }} data-id="element-508">
        <div className="mp-emg-family" style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: 0,
        maxWidth: '1000px',
        margin: '0 auto',
        alignItems: 'center'
      }} data-id="element-509">
          {/* Left: text content */}
          <div className="mp-emg-family-left" style={{
          padding: '40px'
        }} data-id="element-510">
            <div style={{
            fontSize: '12px',
            color: '#7DC920',
            letterSpacing: '3px',
            fontWeight: 700,
            marginBottom: '16px'
          }} data-id="element-511">
              FAMILIAS QUE NECESITAN AYUDA
            </div>
            <h2 className="mp-emg-family-title" style={{
            fontSize: '32px',
            fontWeight: 800,
            color: 'white',
            lineHeight: 1.2,
            marginBottom: '20px'
          }} data-id="element-512">
              Detrás de Cada Número, Hay una Familia
            </h2>
            <p className="mp-emg-family-text" style={{
            fontSize: '16px',
            color: 'rgba(255,255,255,0.75)',
            lineHeight: 1.7,
            marginBottom: '32px'
          }} data-id="element-513">
              Más de 12,000 personas perdieron su hogar en segundos. Familias
              con niños, adultos mayores y personas vulnerables que hoy
              necesitan alimento, refugio y esperanza.
            </p>
            <div className="mp-emg-family-stats" style={{
            display: 'flex',
            gap: '32px'
          }} data-id="element-514">
              {[{
              number: '+12,000',
              label: 'Desplazados',
              color: '#7DC920'
            }, {
              number: '+1,500',
              label: 'Fallecidos',
              color: 'white'
            }, {
              number: '24+',
              label: 'Países ayudando',
              color: '#a8e063'
            }].map(s => <div key={s.label} className="mp-emg-family-stat" data-id="element-515">
                  <div className="mp-emg-family-stat-num" style={{
                fontSize: '28px',
                fontWeight: 800,
                color: s.color
              }} data-id="element-516">
                    {s.number}
                  </div>
                  <div style={{
                fontSize: '12px',
                color: '#999'
              }} data-id="element-517">
                    {s.label}
                  </div>
                </div>)}
            </div>
          </div>

          {/* Right: image */}
          <div className="mp-emg-family-img" style={{
          position: 'relative',
          height: '400px',
          overflow: 'hidden'
        }} data-id="element-518">
            <img src="https://images.unsplash.com/photo-1469571486292-0ba58a3f068b?w=800&q=80" alt="Familia afectada por la emergencia" style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover'
          }} data-id="element-519" />
            <div style={{
            position: 'absolute',
            inset: 0,
            background: 'linear-gradient(to right, rgba(26,26,26,0.4) 0%, transparent 100%)'
          }} data-id="element-520" />
          </div>
        </div>
      </section>

      {/* SECTION 6: DONATION CTA */}
      <section className="mp-emg-cta-section" style={{
      background: 'linear-gradient(135deg, #1a3a0a 0%, #2d5a1b 100%)',
      padding: '80px 20px',
      textAlign: 'center'
    }} data-id="element-521">
        <h2 className="mp-emg-cta-title" style={{
        fontSize: '42px',
        fontWeight: 800,
        color: 'white',
        marginBottom: '16px'
      }} data-id="element-522">
          Tu Donación Salva Vidas
        </h2>
        <p className="mp-emg-cta-subtitle" style={{
        fontSize: '18px',
        color: 'rgba(255,255,255,0.85)',
        marginBottom: '40px'
      }} data-id="element-523">
          Cada aporte llega directamente a las familias venezolanas más
          afectadas por esta tragedia.
        </p>
        <div className="mp-emg-btns flex flex-wrap justify-center gap-4" data-id="element-524">
          <Link to="/donar-venezuela" className="mp-emg-btn" style={{
          background: 'white',
          color: '#2d5a1b',
          fontWeight: 700,
          padding: '18px 48px',
          borderRadius: '12px',
          fontSize: '18px',
          textDecoration: 'none',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }} data-id="element-525">
            <HandCoins size={20} color="#2d5a1b" data-id="element-526" /> Donar Ahora
          </Link>
          <button type="button" className="mp-emg-btn" onClick={() => {
          if (navigator.share) {
            navigator.share({
              title: 'Venezuela Emergencia',
              text: 'Venezuela necesita tu ayuda.',
              url: window.location.href
            });
          } else {
            navigator.clipboard.writeText(window.location.href).then(() => {
              alert('¡Enlace copiado!');
            });
          }
        }} style={{
          background: 'transparent',
          color: 'white',
          border: '2px solid white',
          fontWeight: 700,
          padding: '18px 48px',
          borderRadius: '12px',
          fontSize: '18px',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }} data-id="element-527">
            <Share2 size={20} data-id="element-528" /> Compartir esta página
          </button>
        </div>
      </section>

      {/* SECTION 7: VOLUNTEER FORM */}
      <section id="voluntario" className="mp-emg-vol-section" style={{
      background: '#f9f9f9',
      padding: '80px 20px'
    }} data-id="element-529">
        <p style={sectionLabel} data-id="element-530">Únete al esfuerzo</p>
        <h2 className="mp-emg-form-title" style={{
        fontSize: '36px',
        fontWeight: 700,
        textAlign: 'center',
        marginBottom: '8px',
        color: '#1a1a1a'
      }} data-id="element-531">
          ¿Quieres Ser Voluntario?
        </h2>
        <p style={{
        fontSize: '16px',
        color: '#666',
        textAlign: 'center',
        marginBottom: '48px'
      }} data-id="element-532">
          Regístrate y te contactaremos con formas concretas de ayudar
        </p>
        <form action="https://formspree.io/f/mojolpgn" method="POST" onSubmit={handleVolunteerSubmit} className="mp-emg-form" style={{
        maxWidth: '560px',
        margin: '0 auto',
        background: 'white',
        borderRadius: '20px',
        padding: '40px',
        boxShadow: '0 8px 32px rgba(0,0,0,0.08)'
      }} data-id="element-533">
          <input type="hidden" name="_subject" value="Nuevo Voluntario - Emergencia Venezuela" data-id="element-534" />
          <input type="text" name="nombre" required placeholder="Tu nombre completo" aria-label="Nombre completo" className="mp-emg-field" style={fieldStyle} data-id="element-535" />
          <input type="email" name="email" required placeholder="tu@email.com" aria-label="Email" className="mp-emg-field" style={fieldStyle} data-id="element-536" />
          <input type="text" name="pais" placeholder="¿Desde dónde nos escribes?" aria-label="País de residencia" className="mp-emg-field" style={fieldStyle} data-id="element-537" />
          <select name="como_ayudar" aria-label="¿Cómo quieres ayudar?" defaultValue="" className="mp-emg-field" style={fieldStyle} data-id="element-538">
            <option value="" data-id="element-539">Selecciona una opción</option>
            <option value="donacion" data-id="element-540">Donación económica</option>
            <option value="difusion" data-id="element-541">Difusión en redes sociales</option>
            <option value="organizaciones" data-id="element-542">Contacto con organizaciones</option>
            <option value="otro" data-id="element-543">Otros</option>
          </select>
          <textarea name="comentario" rows={4} placeholder="¿Algo más que quieras contarnos? (opcional)" aria-label="Comentario" className="mp-emg-field mp-emg-textarea" style={{
          ...fieldStyle,
          fontFamily: 'inherit',
          resize: 'vertical',
          minHeight: '100px',
          maxHeight: '200px'
        }} data-id="element-544" />
          <button type="submit" className="mp-emg-submit" style={{
          background: '#2d5a1b',
          color: 'white',
          width: '100%',
          padding: '16px',
          borderRadius: '10px',
          fontSize: '16px',
          fontWeight: 700,
          border: 'none',
          cursor: 'pointer'
        }} data-id="element-545">
            Quiero Ayudar →
          </button>
          {formSubmitted && <p role="status" style={{
          color: '#2d5a1b',
          fontWeight: 600,
          textAlign: 'center',
          marginTop: '20px'
        }} data-id="element-546">
              ¡Gracias! Te contactaremos pronto.
            </p>}
        </form>
      </section>
    </main>;
}